/**
 * App Install Prompt - Shows a popup to encourage users to download the app
 * Detects Android/iOS and shows the appropriate store link
 * Only shows once, respects "Don't show again", and hides in TWA/standalone mode
 */
import React, { useState, useEffect } from 'react';
import { X, Smartphone, Download } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { useAuth } from '../contexts/AuthContext';

const GOOGLE_PLAY_URL = 'https://play.google.com/store/apps/details?id=it.rvincente.app.twa';
const APPLE_STORE_URL = null; // Will be added when available

const detectDevice = () => {
  const ua = navigator.userAgent || navigator.vendor || window.opera;
  if (/android/i.test(ua)) return 'android';
  if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return 'ios';
  return 'desktop';
};

const isRunningAsNativeApp = () => {
  // Check Capacitor native platform first
  try { if (Capacitor.isNativePlatform()) return true; } catch {}
  // Fallback checks for PWA/TWA
  return window.matchMedia('(display-mode: standalone)').matches
    || window.navigator.standalone === true
    || document.referrer.includes('android-app://');
};

const AppInstallPrompt = () => {
  const { user } = useAuth();
  const [show, setShow] = useState(false);
  const [device, setDevice] = useState('desktop');

  useEffect(() => {
    // Gate: NEVER show download push to guests. Meta attribution (fbclid)
    // is lost when the user bounces to the Play/App Store — we must wait
    // until CompleteRegistration has fired server-side via CAPI.
    if (!user) return;

    // Don't show if already running as native app or TWA/standalone
    if (isRunningAsNativeApp()) return;

    // Don't show if user dismissed before
    if (localStorage.getItem('app_install_dismissed')) return;

    const detectedDevice = detectDevice();
    setDevice(detectedDevice);

    // Only show for mobile devices
    if (detectedDevice === 'desktop') return;

    // Show after a short delay
    const timer = setTimeout(() => setShow(true), 2000);
    return () => clearTimeout(timer);
  }, [user]);

  const handleDismiss = () => {
    localStorage.setItem('app_install_dismissed', 'true');
    setShow(false);
  };

  const handleInstall = () => {
    if (device === 'android' && GOOGLE_PLAY_URL) {
      window.open(GOOGLE_PLAY_URL, '_blank');
    } else if (device === 'ios' && APPLE_STORE_URL) {
      window.open(APPLE_STORE_URL, '_blank');
    }
    handleDismiss();
  };

  if (!user) return null;
  if (!show) return null;

  const isAndroid = device === 'android';
  const hasStoreLink = isAndroid ? !!GOOGLE_PLAY_URL : !!APPLE_STORE_URL;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999]"
        onClick={handleDismiss}
      />

      {/* Popup */}
      <div className="fixed bottom-0 left-0 right-0 z-[10000] animate-slide-up" data-testid="app-install-prompt">
        <div className="mx-3 mb-3 rounded-3xl bg-[#1E1E1E] border border-white/10 shadow-2xl overflow-hidden">
          {/* Header gradient bar */}
          <div className="h-1 bg-gradient-to-r from-[#F5A623] via-[#FF6B6B] to-[#F5A623]" />
          
          <div className="p-5">
            {/* Close button */}
            <button
              onClick={handleDismiss}
              className="absolute top-4 right-4 p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
              data-testid="dismiss-install-prompt"
            >
              <X className="w-4 h-4 text-gray-400" />
            </button>

            {/* Content */}
            <div className="flex items-start gap-4">
              {/* App Icon */}
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#F5A623] to-[#FF6B6B] flex items-center justify-center flex-shrink-0 shadow-lg shadow-[#F5A623]/20">
                <Smartphone className="w-8 h-8 text-[#1A1A1A]" />
              </div>

              <div className="flex-1 min-w-0">
                <h3 className="text-white font-black text-lg" style={{fontFamily: 'Barlow Condensed'}}>
                  SCARICA L'APP
                </h3>
                <p className="text-gray-400 text-sm mt-1 leading-relaxed">
                  Ricevi notifiche in tempo reale sulle analisi e accedi pi&ugrave; velocemente!
                </p>
              </div>
            </div>

            {/* Action buttons */}
            <div className="mt-5 space-y-3">
              {hasStoreLink ? (
                <button
                  onClick={handleInstall}
                  className="w-full py-4 rounded-2xl bg-gradient-to-r from-[#F5A623] to-[#FF6B6B] text-[#1A1A1A] font-black text-base uppercase tracking-wider flex items-center justify-center gap-3 shadow-lg shadow-[#F5A623]/30 hover:shadow-[#F5A623]/50 transition-all active:scale-[0.98]"
                  style={{fontFamily: 'Barlow Condensed'}}
                  data-testid="install-app-btn"
                >
                  <Download className="w-5 h-5" />
                  {isAndroid ? 'SCARICA DA GOOGLE PLAY' : 'SCARICA DA APP STORE'}
                </button>
              ) : (
                <div className="w-full py-4 rounded-2xl bg-white/5 border border-white/10 text-center">
                  <p className="text-gray-300 font-bold text-sm">
                    {isAndroid ? 'Disponibile su Google Play' : 'Presto disponibile su App Store'}
                  </p>
                  <p className="text-gray-500 text-xs mt-1">
                    {isAndroid ? '' : 'Ti avviseremo quando sar\u00e0 pronta!'}
                  </p>
                </div>
              )}

              <button
                onClick={handleDismiss}
                className="w-full py-3 rounded-2xl text-gray-500 text-sm font-medium hover:text-gray-400 transition-colors"
                data-testid="skip-install-btn"
              >
                Non mostrare pi&ugrave;
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AppInstallPrompt;
